/**
 * Leave Type Badge Color Mapping
 *
 * Canonical color assignments for leave-type badges used across the app:
 * Leave Management module, Attendance Table (parent & child rows),
 * admin calendar, and reports. Row-surface helpers
 * (`getLeaveTypeGroupRowClass`) keep PLC leave accordions on the same hue
 * family without painting children at badge intensity.
 *
 * Color strategy: name-based `.includes()` matching. Order matters —
 * more specific patterns (e.g. "urgent leave") must precede broader
 * ones (e.g. "annual") to avoid false matches.
 *
 * `getLeaveTypeBadgeClass()` also accepts abbreviations (AL, OSL, etc.)
 * via an internal abbreviation-to-full-name lookup.
 */

const LEAVE_TYPE_COLORS = [
  { match: (n) => n.includes('urgent leave'),                                        classes: 'bg-orange-100 text-orange-700 ring-orange-600/20' },
  { match: (n) => n.includes('home leave'),                                           classes: 'bg-sky-100 text-sky-700 ring-sky-600/20' },
  { match: (n) => n.includes('annual') || n.includes('vacation'),                     classes: 'bg-blue-100 text-blue-700 ring-blue-600/20' },
  { match: (n) => n.includes('outpatient') || (n.includes('sick') && !n.includes('hospitalisation')), classes: 'bg-amber-100 text-amber-700 ring-amber-600/20' },
  { match: (n) => n.includes('hospitalisation'),                                      classes: 'bg-red-100 text-red-700 ring-red-600/20' },
  { match: (n) => n.includes('maternity'),                                            classes: 'bg-pink-100 text-pink-700 ring-pink-600/20' },
  { match: (n) => n.includes('paternity'),                                            classes: 'bg-purple-100 text-purple-700 ring-purple-600/20' },
  { match: (n) => n.includes('shared parental'),                                      classes: 'bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-600/20' },
  { match: (n) => n.includes('adoption'),                                             classes: 'bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-600/20' },
  { match: (n) => n.includes('extended') && n.includes('childcare'),                  classes: 'bg-emerald-100 text-emerald-700 ring-emerald-600/20' },
  { match: (n) => n.includes('childcare'),                                            classes: 'bg-green-100 text-green-700 ring-green-600/20' },
  { match: (n) => n.includes('infant care'),                                          classes: 'bg-green-100 text-green-700 ring-green-600/20' },
  { match: (n) => n.includes('marriage'),                                             classes: 'bg-rose-100 text-rose-700 ring-rose-600/20' },
  { match: (n) => n.includes('compassionate') || n.includes('bereavement'),           classes: 'bg-gray-100 text-gray-700 ring-gray-600/20' },
  // MOM-Sandwich-Rule forfeiture: the unpaid-leave window swallows a
  // PH so the PH allowance becomes a deduction. Surfaces as a chip
  // labelled "Unpaid PH (<holiday>)" on the Step 3 deduction panel.
  // Must precede the broader `unpaid` matcher.
  { match: (n) => n.includes('unpaid') && n.includes('ph'),                           classes: 'bg-red-50 text-red-700 ring-red-600/20' },
  { match: (n) => n.includes('unpaid'),                                               classes: 'bg-slate-100 text-slate-700 ring-slate-600/20' },
  { match: (n) => n === 'off in lieu' || n.includes('oil'),                           classes: 'bg-teal-100 text-teal-700 ring-teal-600/20' },
  { match: (n) => n.includes('external activity') || n.includes('external'),          classes: 'bg-cyan-100 text-cyan-700 ring-cyan-600/20' },
  { match: (n) => n.includes('work from home') || n.includes('wfh'),                  classes: 'bg-indigo-100 text-indigo-700 ring-indigo-600/20' },
  { match: (n) => n === 'on course',                                                  classes: 'bg-violet-100 text-violet-700 ring-violet-600/20' },
  { match: (n) => n.includes('national service'),                                     classes: 'bg-lime-100 text-lime-700 ring-lime-600/20' },
  { match: (n) => n.includes('study'),                                                classes: 'bg-violet-100 text-violet-700 ring-violet-600/20' },
  { match: (n) => n.includes('jury'),                                                 classes: 'bg-gray-100 text-gray-700 ring-gray-600/20' },
  { match: (n) => n.includes('medical'),                                              classes: 'bg-amber-100 text-amber-700 ring-amber-600/20' },
  { match: (n) => n.includes('paid leave') && n.includes('ph'),                       classes: 'bg-red-50 text-red-700 ring-red-600/20' },
  { match: (n) => n.includes('absent'),                                               classes: 'bg-slate-100 text-slate-700 ring-slate-600/20' },
  // Recovery (Day / AM / PM): warm yellow reads as recuperation/rest
  // while avoiding the existing blue AL, amber/medical, and orange/urgent
  // leave chips.
  { match: (n) => n.includes('recovery'),                                             classes: 'bg-yellow-50 text-yellow-800 ring-yellow-600/30' },
];

const DEFAULT_CLASSES = 'bg-stone-100 text-stone-700 ring-stone-600/20';

const ABBR_TO_FULL_NAME = {
  'al':   'annual leave',
  'osl':  'outpatient sick leave',
  'mc':   'medical leave',
  'hl':   'hospitalisation leave',
  'ugl':  'urgent leave',
  'hml':  'home leave',
  'ml':   'maternity leave',
  'pl':   'paternity leave',
  'spl':  'shared parental leave',
  'adl':  'adoption leave',
  'ccl':  'childcare leave',
  'ccl(nc)': 'childcare leave (non-citizen)',
  'eccl': 'extended childcare leave',
  'uicl': 'unpaid infant care leave',
  'mrl':  'marriage leave',
  'cl':   'compassionate leave',
  'cl(if)': 'compassionate leave (immediate family)',
  'cl(gp)': 'compassionate leave (grandparent)',
  'ul':   'unpaid leave',
  'oil':  'off in lieu',
  'ea':   'external activity',
  'wfh':  'work from home',
  'oc':   'on course',
  'nsl':  'national service leave',
  'eml':  'emergency leave',
  'stl':  'study leave',
  'abs':  'absent',
};

/**
 * Returns Tailwind badge classes for a given leave type name.
 * Used by <LeaveTypeBadge> and the admin calendar grid.
 *
 * Accepts full names ("Annual Leave") or abbreviations ("AL").
 *
 * @param {string} leaveTypeName
 * @returns {string} Tailwind classes (bg + text + ring)
 */
export const getLeaveTypeBadgeClass = (leaveTypeName) => {
  let nameLower = (leaveTypeName || '').toLowerCase().trim();
  if (ABBR_TO_FULL_NAME[nameLower]) {
    nameLower = ABBR_TO_FULL_NAME[nameLower];
  }
  const entry = LEAVE_TYPE_COLORS.find((e) => e.match(nameLower));
  return entry ? entry.classes : DEFAULT_CLASSES;
};

/**
 * Returns only the background class for legend swatches / calendar dots.
 * Strips text-* and ring-* classes, keeping only bg-*.
 *
 * @param {string} leaveTypeName
 * @returns {string} Single Tailwind bg class
 */
export const getLeaveTypeBgClass = (leaveTypeName) => {
  const full = getLeaveTypeBadgeClass(leaveTypeName);
  return full.split(' ').find((c) => c.startsWith('bg-')) || 'bg-stone-100';
};

/**
 * Badge fills stay at -100 so chips remain the identity signal. Report group
 * rows drop one shade and add opacity so a 12-row expand does not become a
 * solid colour slab. Keys MUST be complete class strings — Tailwind cannot
 * see interpolated `bg-${hue}-50/30` names.
 */
const LEAVE_TYPE_ROW_SURFACES = {
  'bg-orange-100': { parent: 'bg-orange-50/70', child: 'bg-orange-50/30' },
  'bg-sky-100':    { parent: 'bg-sky-50/70',    child: 'bg-sky-50/30' },
  'bg-blue-100':   { parent: 'bg-blue-50/70',   child: 'bg-blue-50/30' },
  'bg-amber-100':  { parent: 'bg-amber-50/70',  child: 'bg-amber-50/30' },
  'bg-red-100':    { parent: 'bg-red-50/70',    child: 'bg-red-50/30' },
  'bg-pink-100':   { parent: 'bg-pink-50/70',   child: 'bg-pink-50/30' },
  'bg-purple-100': { parent: 'bg-purple-50/70', child: 'bg-purple-50/30' },
  'bg-fuchsia-100':{ parent: 'bg-fuchsia-50/70',child: 'bg-fuchsia-50/30' },
  'bg-emerald-100':{ parent: 'bg-emerald-50/70',child: 'bg-emerald-50/30' },
  'bg-green-100':  { parent: 'bg-green-50/70',  child: 'bg-green-50/30' },
  'bg-rose-100':   { parent: 'bg-rose-50/70',   child: 'bg-rose-50/30' },
  'bg-gray-100':   { parent: 'bg-gray-50/70',   child: 'bg-gray-50/30' },
  'bg-red-50':     { parent: 'bg-red-50/70',    child: 'bg-red-50/30' },
  'bg-slate-100':  { parent: 'bg-slate-50/70',  child: 'bg-slate-50/30' },
  'bg-teal-100':   { parent: 'bg-teal-50/70',   child: 'bg-teal-50/30' },
  'bg-cyan-100':   { parent: 'bg-cyan-50/70',   child: 'bg-cyan-50/30' },
  'bg-indigo-100': { parent: 'bg-indigo-50/70', child: 'bg-indigo-50/30' },
  'bg-violet-100': { parent: 'bg-violet-50/70', child: 'bg-violet-50/30' },
  'bg-lime-100':   { parent: 'bg-lime-50/70',   child: 'bg-lime-50/30' },
  'bg-yellow-50':  { parent: 'bg-yellow-50/70', child: 'bg-yellow-50/30' },
  'bg-stone-100':  { parent: 'bg-stone-50/70',  child: 'bg-stone-50/30' },
};

/**
 * Subtle row-surface tint for a leave-type group in a dense report table.
 * Lighter than the badge fill so chips stay the primary visual signal.
 *
 * @param {string} leaveTypeName
 * @param {'parent'|'child'} [role='child']
 * @returns {string} Tailwind bg class
 */
export const getLeaveTypeRowSurfaceClass = (leaveTypeName, role = 'child') => {
  const bg = getLeaveTypeBgClass(leaveTypeName);
  const surfaces = LEAVE_TYPE_ROW_SURFACES[bg] || LEAVE_TYPE_ROW_SURFACES['bg-stone-100'];
  return surfaces[role] || surfaces.child;
};

/**
 * Full `<tr>` className for a leave-type accordion row.
 * Hover is the dense-table neutral gray — never a theme-coloured hover.
 *
 * @param {string} leaveTypeName
 * @param {{ role?: 'parent'|'child' }} [options]
 * @returns {string}
 */
export const getLeaveTypeGroupRowClass = (leaveTypeName, { role = 'child' } = {}) => {
  const surface = getLeaveTypeRowSurfaceClass(leaveTypeName, role);
  return role === 'parent'
    ? `${surface} table-row-dense cursor-pointer`
    : `${surface} table-row-dense`;
};

/**
 * Canonical legend items derived from the color mapping above.
 * Each entry carries the full name, abbreviation, and a bg + border class
 * pair for swatch rendering. Used by the Leave Calendar and (optionally)
 * the Payroll Adjust & Lock table.
 */
export const LEAVE_LEGEND_ITEMS = [
  { label: 'Annual Leave',             abbr: 'AL',     color: 'bg-blue-100 border-blue-300' },
  { label: 'Outpatient Sick Leave',    abbr: 'OSL',    color: 'bg-amber-100 border-amber-300' },
  { label: 'Medical Leave',            abbr: 'MC',     color: 'bg-amber-100 border-amber-300' },
  { label: 'Hospitalisation Leave',    abbr: 'HL',     color: 'bg-red-100 border-red-300' },
  { label: 'Urgent Leave',             abbr: 'UGL',    color: 'bg-orange-100 border-orange-300' },
  { label: 'Home Leave',               abbr: 'HML',    color: 'bg-sky-100 border-sky-300' },
  { label: 'Maternity Leave',          abbr: 'ML',     color: 'bg-pink-100 border-pink-300' },
  { label: 'Paternity Leave',          abbr: 'PL',     color: 'bg-purple-100 border-purple-300' },
  { label: 'Shared Parental Leave',    abbr: 'SPL',    color: 'bg-fuchsia-100 border-fuchsia-300' },
  { label: 'Adoption Leave',           abbr: 'ADL',    color: 'bg-fuchsia-100 border-fuchsia-300' },
  { label: 'Childcare Leave',          abbr: 'CCL',    color: 'bg-green-100 border-green-300' },
  { label: 'Ext. Childcare Leave',     abbr: 'ECCL',   color: 'bg-emerald-100 border-emerald-300' },
  { label: 'Unpaid Infant Care Leave', abbr: 'UICL',   color: 'bg-green-100 border-green-300' },
  { label: 'Marriage Leave',           abbr: 'MRL',    color: 'bg-rose-100 border-rose-300' },
  { label: 'Compassionate Leave',      abbr: 'CL',     color: 'bg-gray-100 border-gray-300' },
  { label: 'Unpaid Leave',             abbr: 'UL',     color: 'bg-slate-100 border-slate-300' },
  { label: 'Off in Lieu',              abbr: 'OIL',    color: 'bg-teal-100 border-teal-300' },
  { label: 'External Activity',        abbr: 'EA',     color: 'bg-cyan-100 border-cyan-300' },
  { label: 'Work From Home',           abbr: 'WFH',    color: 'bg-indigo-100 border-indigo-300' },
  { label: 'On Course',                abbr: 'OC',     color: 'bg-violet-100 border-violet-300' },
  { label: 'National Service Leave',   abbr: 'NSL',    color: 'bg-lime-100 border-lime-300' },
  { label: 'Emergency Leave',          abbr: 'EML',    color: 'bg-orange-100 border-orange-300' },
  { label: 'Study Leave',              abbr: 'STL',    color: 'bg-violet-100 border-violet-300' },
  { label: 'Public Holiday',           abbr: 'PH',     color: 'bg-red-50 border-red-300' },
  { label: 'Paid Leave (PH)',          abbr: 'PH',     color: 'bg-red-50 border-red-300' },
  { label: 'Absent',          abbr: 'ABS',    color: 'bg-slate-100 border-slate-300' },
];
